import React, { useState, useContext, useEffect } from 'react';
import {
  Row, Col, Form, Input, Button, Avatar, Card, Upload, Select, message, notification, Tag,
} from 'antd';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { CHANGE_PASSWORD, GET_ME } from '../../graphql';
import { uploadFile } from '../../upload';
import PasswordModal from './PasswordModal';
import { AuthContext } from '../../appContext/AuthContext';
import { LOGIN } from '../../routes';
import ViewSpinner from '../../components/viewSpinner/ViewSpinner';
import './styles.less';

const { Item: FormItem } = Form;
const { Option } = Select;

const RoleTag = (props) => {
  // eslint-disable-next-line react/prop-types
  const { label, onClose } = props;
  return (
    <Tag
      color="green"
      onClose={onClose}
      style={{ marginBottom: '-2px' }}
    >
      {label}
    </Tag>
  );
};

const Profile = () => {
  const [changePassword, showChangePassword] = useState(false);
  const { revokeAuth } = useContext(AuthContext);
  // `me` rather than a query taking an id — the account is the token's.
  const { data: userPayload, loading: loadingUser, refetch } = useQuery(GET_ME);
  const client = useApolloClient();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [mutateChangePassword, { loading: changePassLoading }] = useMutation(CHANGE_PASSWORD);
  const [profile, setProfile] = useState(null);
  const [profileTeacher, setProfileTeacher] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!loadingUser && userPayload) {
      const { me } = userPayload;
      setUser(me);
      setProfileTeacher(me.profileTeacher);
      setProfile(me.profileTeacher?.profileImg);
    }
  }, [userPayload, loadingUser]);
  /**
   * The picture goes straight to storage and the API is told afterwards, so
   * the image bytes never pass through it. `targetId` is ignored for a
   * profile image — the server applies it to the caller.
   */
  const handleFileUpload = async ({ file, onSuccess, onError }) => {
    setUploadLoading(true);
    try {
      const upload = await uploadFile({
        client, file, target: 'PROFILE_IMAGE', targetId: user.id,
      });
      setProfile(upload.url);
      // The stored value is an object path; the readable URL is signed per
      // request, so the record is re-read rather than patched locally.
      await refetch();
      message.success('Profile picture updated successfully');
      onSuccess({ url: upload.url });
    } catch (error) {
      onError(error);
      message.error(error.message || 'Error, could not update picture');
    } finally {
      setUploadLoading(false);
    }
  };

  const getCohortSubjects = (cohortId, subjects) => subjects
    .filter((subject) => subject.cohortId === cohortId)
    .map(({ name }) => name);
  const handleFormSubmit = async (inputValue) => {
    const { oldPassword, newPassword } = inputValue;
    try {
      await mutateChangePassword({
        variables: {
          input: { newPassword, oldPassword },
        },
      });
      notification.success({ message: 'Password successfully changed', description: 'Please login with your new password' });
      revokeAuth();
      navigate(LOGIN);
    } catch (error) {
      message.error('Error, could not change password');
    }
  };
  return (
    <div className="profile-teacher">
      <PasswordModal
        handleCancel={() => showChangePassword(false)}
        loading={changePassLoading}
        visible={changePassword}
        handleFormSubmit={handleFormSubmit}
      />
      { !loadingUser && user && (
      <>
        <Card className="profile-card" title="Teacher Information">
          <Row>
            <Col md={10} xs={24}>
              <div className="photo-card">
                <div className="avatar-wrapper">
                  <Avatar src={profile} size="large" className="teacher-avatar" />
                </div>
                <Upload
                  customRequest={handleFileUpload}
                  accept="image/png,image/jpeg,image/jpg"
                  showUploadList={false}
                >
                  <Button
                    loading={uploadLoading}
                    className="upload-btn"
                    icon={<UploadOutlined />}
                  >
                    Change Picture
                  </Button>
                </Upload>
              </div>
            </Col>
            <Col md={14} xs={24}>
              <Form layout="vertical">
                <FormItem initialValue={user.firstName} label="First Name" name="firstName">
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={user.lastName} label="Last Name" name="lastName">
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={profileTeacher?.phoneNumber || 'None'} label="Phone Number" name="phoneNumber">
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={user.email} label="Email" name="email">
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={profileTeacher?.address || 'Not set'} label="Address" name="address">
                  <Input disabled />
                </FormItem>
                <FormItem
                  initialValue={profileTeacher?.doe ? moment(profileTeacher.doe).format('MMM Do YYYY') : 'Not set'}
                  label="Date Employment"
                  name="doe"
                >
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={[user.role]} label="Role" name="role">
                  <Select disabled tagRender={RoleTag} mode="multiple">
                    <Option key="1" value="INSTRUCTOR">INSTRUCTOR</Option>
                    <Option key="2" value="ADMIN">ADMIN</Option>
                  </Select>
                </FormItem>
              </Form>
              <Button onClick={() => showChangePassword(true)} className="change-pass-btn">Change Password</Button>
            </Col>
          </Row>
        </Card>
        <Card className="cohort-card" title="Teacher Cohort and Subjects">
          <Form layout="vertical">
            {user.cohorts.map(({ name, id: cohortId }, idx) => (
              <Row key={cohortId} className="no-flex-column">
                <Col xs={24} sm={12}>
                  <FormItem initialValue={name} name={`cohort_${cohortId}`} label={idx === 0 ? 'Cohort' : ''}>
                    <Input disabled />
                  </FormItem>
                </Col>
                <Col xs={24} sm={12}>
                  <FormItem initialValue={getCohortSubjects(cohortId, user.subjects)} name={`subject_${cohortId}`} label={idx === 0 ? 'Subjects' : ''}>
                    <Select disabled mode="multiple">
                      <Option key="1" value="1">Maths</Option>
                    </Select>
                  </FormItem>
                </Col>
              </Row>
            ))}
          </Form>
        </Card>
      </>
      )}
      {loadingUser && <ViewSpinner />}
    </div>
  );
};

export default Profile;
